import { Component, inject } from '@angular/core'
import { Store } from '@ngrx/store'
import { DialogService } from '@ziptility/components/dialogs'
import { TaskAPIService } from '@ziptility/modules'
import { BvAlertService } from '@ziptility/bv-alert'
import { getMySelectedMap, getUser } from '@ziptility/store'
import { firstValueFrom } from 'rxjs'
import { TaskMap } from './map.component'
import { TaskListActionsComponent } from '.'

import { Task, TaskBoard, UpdateTaskGQL, TaskBoardType } from '@ziptility/graphql'
import { SwimLane, BoardsStore } from '../boards/boards.store'
import { CdkDragDrop } from '@angular/cdk/drag-drop'
import { saveDispatchLayout } from '@ziptility/store/preference/actions'
import { ActivatedRoute, Router } from '@angular/router'
import { BoardSwimlaneComponent } from '../components/swimlane/swimlane.component'
import { toSignal } from '@angular/core/rxjs-interop'

@Component({
  selector: 'task-map-container',
  template: `
    @if (map(); as map) {
      <div class="w-full h-full overflow-auto flex flex-col items-stretch relative">
        <div class="flex flex-row w-full h-full overflow-auto">
          <div class="flex w-full">
            <div class="h-full flex-grow relative">
              <task-list-map [selectedMap]="map" (onSelections)="onTasksSelected($event.ids)"></task-list-map>
              @if (taskBoardStore.selectedTaskIds().length) {
                <div class="absolute bottom-5 left-1/2 transform -translate-x-1/2 z-10">
                  <task-list-actions
                    [selections]="taskBoardStore.selectedTaskIds()"
                    [boards]="taskBoardStore.boards()"
                    (onBoardChange)="changeBoard($event)"
                    (onDueDateSelected)="onSaveSelectedTasksDueDate($event.dueDate)"
                    (onDeleteSelected)="onDeleteSelectedTasks()"
                  ></task-list-actions>
                </div>
              }
            </div>
            <div class="h-full overflow-auto px-3 bg-gray-300">
              @for (swimlane of taskBoardStore.visibleSwimLanes(); track swimlane.id) {
                @defer (on viewport) {
                  <board-swimlane
                    [swimlane]="swimlane"
                    [showSelection]="true"
                    [collapsible]="true"
                    [followerOptions]="taskBoardStore.followerOptions()"
                    (loadNextPage)="
                      taskBoardStore.loadBoardTasks({
                        boardId: swimlane.id,
                        endCursor: swimlane.pageInfo?.endCursor
                      })
                    "
                    (onSelectTask)="selectTask($event.task)"
                  ></board-swimlane>
                } @placeholder {
                  <div class="h-[100px] w-[350px]"></div>
                }
              }
            </div>
          </div>
        </div>
      </div>
    }
  `,
  imports: [TaskMap, TaskListActionsComponent, BoardSwimlaneComponent],
})
export class TaskMapContainer {
  readonly store = inject(Store)
  readonly taskService = inject(TaskAPIService)
  readonly alertService = inject(BvAlertService)
  readonly dialogService = inject(DialogService)
  readonly taskBoardStore = inject(BoardsStore)
  readonly route = inject(ActivatedRoute)
  readonly router = inject(Router)
  readonly updateTask = inject(UpdateTaskGQL)
  readonly user = toSignal(this.store.select(getUser))
  readonly map = toSignal(this.store.select(getMySelectedMap))

  ngOnInit() {
    this.store.dispatch(saveDispatchLayout({ layout: 'map' }))
  }

  onTasksSelected(ids: string[]) {
    this.taskBoardStore.toggleSelections(ids)
  }

  selectTask(task: Task) {
    this.router.navigate(['../', { outlets: { task: [task.id] } }], { relativeTo: this.route })
  }

  async onSaveSelectedTasksDueDate(dueDate: string | undefined) {
    try {
      const str = dueDate != undefined ? new Date(dueDate).toISOString() : ''
      const tasks = this.taskBoardStore.selectedTasks()
      const ids = tasks.map((t) => t.id)
      await firstValueFrom(this.taskService.bulkUpdateDueDate(ids, str))
      this.alertService.success('Updated')
      this.taskBoardStore.refreshEffectedBoards(tasks)
    } catch (err: any) {
      this.alertService.error(err.message)
    }
  }

  async onDeleteSelectedTasks() {
    const confirm = await this.dialogService.confirm({
      header: 'Delete tasks?',
      message: 'Are you sure you want to delete these tasks. This action cannot be undone.',
      confirmText: 'Delete',
    })
    if (!confirm) {
      return
    }

    this.taskBoardStore.deleteSelectedTasks()
  }

  onReorder(swimLane: SwimLane, event: CdkDragDrop<Task[]>) {
    const { previousIndex, currentIndex } = event
    const task = swimLane.tasks[previousIndex]
    this.taskBoardStore.moveTask({
      previousSwimLaneId: swimLane.entityId,
      swimLaneId: swimLane.entityId,
      task,
      previousIndex: previousIndex,
      index: currentIndex,
    })
  }

  async changeBoard({ board }: { board: TaskBoard }) {
    const tasks = this.taskBoardStore.selectedTasks()
    const requests = tasks.map((task: Task) => {
      return firstValueFrom(
        this.updateTask.mutate({
          request: {
            taskId: task.id,
            order: task.order ?? 0,
            assigneeId: board.type === TaskBoardType.User ? board.assigneeId : undefined,
            taskListId: board.type === TaskBoardType.TaskList ? board.taskListId : undefined,
          },
        }),
      )
    })

    await Promise.all(requests)

    this.taskBoardStore.refreshEffectedBoards(tasks)
    this.taskBoardStore.loadBoardTasks({ boardId: board.id })
  }
}
